import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { movieApi, Genre, Director, Movie, Actor, handleApiError } from '../services/api';
import MovieCard from '../components/MovieCard';

const Browse: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'genres' | 'directors' | 'actors'>(
    (searchParams.get('tab') as 'genres' | 'directors' | 'actors') || 'genres'
  );

  const [genres, setGenres] = useState<Genre[]>([]);
  const [directors, setDirectors] = useState<Director[]>([]);
  const [actors, setActors] = useState<Actor[]>([]);
  const [selectedMovies, setSelectedMovies] = useState<Movie[]>([]);
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingMovies, setIsLoadingMovies] = useState(false);
  const [error, setError] = useState('');

  // Filtering and sorting state
  const [sortBy, setSortBy] = useState<'year' | 'title' | 'rating'>('year');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [yearFilter, setYearFilter] = useState<{ min: number; max: number }>({ min: 1900, max: 2024 });
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Handle URL query parameters for direct navigation from MovieDetails
  useEffect(() => {
    const tab = searchParams.get('tab') as 'genres' | 'directors' | 'actors';
    const genre = searchParams.get('genre');
    const director = searchParams.get('director');
    const actor = searchParams.get('actor');
    
    if (tab) {
      setActiveTab(tab);
      
      // If we have a specific category from URL params, fetch movies for it first
      if (genre && tab === 'genres') {
        fetchMoviesByCategory(genre);
        // Load categories without clearing selection
        fetchCategories(tab, false);
      } else if (director && tab === 'directors') {
        fetchMoviesByCategory(director);
        // Load categories without clearing selection
        fetchCategories(tab, false);
      } else if (actor && tab === 'actors') {
        fetchMoviesByCategory(actor);
        // Load categories without clearing selection
        fetchCategories(tab, false);
      } else {
        // No specific category, just load categories normally
        fetchCategories(tab);
      }
    } else {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMovies, sortBy, sortOrder, yearFilter, ratingFilter]);

  // Scroll selected category into view when categories are loaded and selection is made
  useEffect(() => {
    if (selectedCategory && !isLoadingCategories) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const selectedButton = document.querySelector(`button[data-category="${selectedCategory}"]`);
        if (selectedButton) {
          selectedButton.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  }, [selectedCategory, isLoadingCategories]);

  const fetchCategories = async (tab?: 'genres' | 'directors' | 'actors', clearSelection: boolean = true) => {
    const currentTab = tab || activeTab;
    setIsLoadingCategories(true);
    setError('');
    
    // Only clear selection if explicitly requested (for tab changes)
    if (clearSelection) {
      setSelectedMovies([]);
      setSelectedCategory('');
    }

    try {
      if (currentTab === 'genres') {
        const response = await movieApi.getGenres();
        setGenres(response.genres);
      } else if (currentTab === 'directors') {
        const response = await movieApi.getDirectors();
        setDirectors(response.directors);
      } else {
        const response = await movieApi.getActors();
        setActors(response.actors);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const fetchMoviesByCategory = async (category: string) => {
    setIsLoadingMovies(true);
    setError('');
    setSelectedCategory(category);

    try {
      let response;
      if (activeTab === 'genres') {
        response = await movieApi.getMoviesByGenre(category);
      } else if (activeTab === 'directors') {
        response = await movieApi.getMoviesByDirector(category);
      } else {
        response = await movieApi.searchMoviesByActor(category);
      }
      setSelectedMovies(response.movies);
    } catch (err) {
      setError(handleApiError(err));
      setSelectedMovies([]);
    } finally {
      setIsLoadingMovies(false);
    }
  };

  const handleMovieClick = (movie: Movie) => {
    navigate(`/movie/${movie.id}`);
  };

  const handleTabChange = (tab: 'genres' | 'directors' | 'actors') => {
    setActiveTab(tab);
    // Clear any existing category params and set the new tab
    setSearchParams({ tab });
    // Clear movies when switching tabs
    setSelectedMovies([]);
    setSelectedCategory('');
    // Fetch new categories
    fetchCategories(tab);
  };

  const applyFiltersAndSort = () => {
    let filtered = [...selectedMovies];

    // Apply year filter
    filtered = filtered.filter(movie => {
      if (!movie.year) return true;
      return movie.year >= yearFilter.min && movie.year <= yearFilter.max;
    });

    // Apply rating filter
    if (ratingFilter !== 'all') {
      filtered = filtered.filter(movie => {
        switch (ratingFilter) {
          case 'high': // 7.0+ IMDB rating
            return movie.imdb_rating && parseFloat(movie.imdb_rating.split('/')[0]) >= 7.0;
          case 'medium': // 5.0-6.9 IMDB rating
            const rating = movie.imdb_rating ? parseFloat(movie.imdb_rating.split('/')[0]) : 0;
            return rating >= 5.0 && rating < 7.0;
          case 'with-rating': // Has any rating
            return movie.imdb_rating || movie.rotten_tomatoes || movie.metacritic;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'year':
          comparison = (a.year || 0) - (b.year || 0);
          break;
        case 'title':
          comparison = (a.omdb_title || a.cleaned_title).localeCompare(b.omdb_title || b.cleaned_title);
          break;
        case 'rating':
          const aRating = a.imdb_rating ? parseFloat(a.imdb_rating.split('/')[0]) : 0;
          const bRating = b.imdb_rating ? parseFloat(b.imdb_rating.split('/')[0]) : 0;
          comparison = aRating - bRating;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredMovies(filtered);
  };

  const getYearRange = (movies: Movie[]) => {
    const years = movies.map(m => m.year).filter(Boolean) as number[];
    if (years.length === 0) return { min: 1900, max: 2024 };
    return { min: Math.min(...years), max: Math.max(...years) };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Page Content */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.location.href = '/'}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Home
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Browse Movies</h1>
            <div className="w-16"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex justify-center">
            <div className="bg-white rounded-lg shadow-sm p-1 inline-flex">
              <button
                onClick={() => handleTabChange('genres')}
                className={`px-6 py-3 rounded-md font-medium transition-colors ${
                  activeTab === 'genres'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                🎭 By Genre
              </button>
              <button
                onClick={() => handleTabChange('directors')}
                className={`px-6 py-3 rounded-md font-medium transition-colors ${
                  activeTab === 'directors'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                🎬 By Director
              </button>
              <button
                onClick={() => handleTabChange('actors')}
                className={`px-6 py-3 rounded-md font-medium transition-colors ${
                  activeTab === 'actors'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                🎭 By Actor
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mt-0.5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {activeTab === 'genres' ? '🎭 Genres' : activeTab === 'directors' ? '🎬 Directors' : '🎭 Actors'}
              </h2>

              {isLoadingCategories ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(activeTab === 'genres' ? genres : activeTab === 'directors' ? directors : actors).map((item) => (
                    <button
                      key={item.name}
                      data-category={item.name}
                      onClick={() => fetchMoviesByCategory(item.name)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        selectedCategory === item.name
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium capitalize">{item.name}</span>
                        <span className={`text-sm px-2 py-1 rounded-full ${
                          selectedCategory === item.name
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.movie_count}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Movies Grid */}
          <div className="lg:col-span-2">
            {selectedCategory ? (
              <div>
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {selectedCategory} Movies
                      </h2>
                      <p className="text-gray-600">
                        {filteredMovies.length > 0 && `${filteredMovies.length} of ${selectedMovies.length} movies`}
                      </p>
                    </div>
                    
                    {/* Filter Toggle Button */}
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                      </svg>
                      Filters
                    </button>
                  </div>

                  {/* Filter Controls */}
                  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 transition-all duration-300 ${showFilters ? 'block' : 'hidden'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Sort By */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as 'year' | 'title' | 'rating')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="year">Year</option>
                          <option value="title">Title</option>
                          <option value="rating">Rating</option>
                        </select>
                      </div>

                      {/* Sort Order */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                        <select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="desc">Descending</option>
                          <option value="asc">Ascending</option>
                        </select>
                      </div>

                      {/* Rating Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                        <select
                          value={ratingFilter}
                          onChange={(e) => setRatingFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="all">All Movies</option>
                          <option value="high">High (7.0+)</option>
                          <option value="medium">Medium (5.0-6.9)</option>
                          <option value="with-rating">With Ratings</option>
                        </select>
                      </div>

                      {/* Year Range */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Year Range: {yearFilter.min} - {yearFilter.max}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="range"
                            min={getYearRange(selectedMovies).min}
                            max={getYearRange(selectedMovies).max}
                            value={yearFilter.min}
                            onChange={(e) => setYearFilter({ ...yearFilter, min: parseInt(e.target.value) })}
                            className="flex-1"
                          />
                          <input
                            type="range"
                            min={getYearRange(selectedMovies).min}
                            max={getYearRange(selectedMovies).max}
                            value={yearFilter.max}
                            onChange={(e) => setYearFilter({ ...yearFilter, max: parseInt(e.target.value) })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isLoadingMovies ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading movies...</p>
                  </div>
                ) : filteredMovies.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredMovies.map((movie) => (
                      <MovieCard
                        key={movie.id}
                        movie={movie}
                        onClick={() => handleMovieClick(movie)}
                        size="medium"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v7.586l2.707 2.707a1 1 0 01.293.707V19a2 2 0 01-2 2H5a2 2 0 01-2-2v-4a1 1 0 01.293-.707L6 11.586V4a1 1 0 011-1z" />
                    </svg>
                    <p className="text-gray-600">No movies found for "{selectedCategory}"</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21l3-3h8a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2l2 3z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Select a {activeTab === 'genres' ? 'Genre' : activeTab === 'directors' ? 'Director' : 'Actor'}
                </h3>
                <p className="text-gray-600">
                  Choose a {activeTab === 'genres' ? 'genre' : activeTab === 'directors' ? 'director' : 'actor'} from the sidebar to see movies
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Browse;